import React from "react";

import { Grid, Skeleton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { VictoryArea, VictoryAxis, VictoryChart, VictoryLabel } from "victory";
import { ChartData } from "libLegacy/api/models";

import { formatTimeLabel, normalizeTimeValues } from "logic/Charts";
import { AXIS_STYLE, CHART_PADDING, useChartStyles } from "./chartStyles";

const valuePlaceholder = [0, 500, 1000, 1500, 2000, 2500];

/**
 * This chart can be used for any time series with numeric values. The X axis is labeled with the months.

 * @param data - The data to be displayed in the chart.
 * @param isLoading - Whether the data is still loading and a skeleton should be displayed.
 * @param formatLabel - A function to format the Y labels of the chart.
 */
export const ChartEconomy = ({
  data,
  isLoading,
  formatLabel,
}: ChartEconomyProps) => {
  const { classes } = useChartStyles();
  const theme = useTheme();
  const usePlaceholderLabels = data.every(({ value }) => value === 0);

  const Chart = () => (
    <Grid container item>
      <Grid container item className={classes.bordered}>
        <VictoryChart padding={CHART_PADDING}>
          <defs>
            <linearGradient
              id="gradient-chartEconomy"
              x1="112"
              y1="0"
              x2="112"
              y2="220"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor={theme.palette.charts.secondary} />
              <stop
                offset="1"
                stopColor={theme.palette.charts.secondary}
                stopOpacity="0.22"
              />
            </linearGradient>
          </defs>
          <VictoryAxis
            tickFormat={formatTimeLabel}
            style={AXIS_STYLE}
            tickValues={data.map((dataPoint) => normalizeTimeValues(dataPoint))}
            tickLabelComponent={<VictoryLabel angle={-45} dx={-8} dy={-5} />}
          />
          <VictoryAxis
            dependentAxis
            style={AXIS_STYLE}
            tickValues={usePlaceholderLabels ? valuePlaceholder : undefined}
            tickFormat={formatLabel}
          />
          {!isLoading && data.length > 0 && (
            <VictoryArea
              data={data}
              style={{ data: { fill: `url(#gradient-chartEconomy)` } }}
              x={normalizeTimeValues}
              y={({ value }) => +value}
            />
          )}
        </VictoryChart>
      </Grid>
    </Grid>
  );

  if (isLoading) {
    return (
      <Skeleton variant="rectangular">
        <Chart />
      </Skeleton>
    );
  } else return <Chart />;
};

export type ChartEconomyProps = {
  data: ChartData[];
  isLoading?: boolean;
  formatLabel: (value: ChartData["value"]) => string;
};
